import React, { useMemo, useState, useEffect } from "react";
import { apiPost } from "../api";
import type { Locale } from "../i18n";
import { t } from "../i18n";
import { BtnSpinner } from "../components/BtnSpinner";

function getPasswordStrengthScore(pw: string): number {
  const p = pw ?? "";
  if (p.length === 0) return 0;

  const hasLower = /[a-z]/.test(p);
  const hasUpper = /[A-Z]/.test(p);
  const hasDigit = /[0-9]/.test(p);
  const hasSymbol = /[^a-zA-Z0-9]/.test(p);
  const variety = [hasLower, hasUpper, hasDigit, hasSymbol].filter(Boolean).length;

  let score = 0;
  if (p.length >= 8) score += 1;
  if (p.length >= 12) score += 1;
  if (variety >= 3) score += 1;
  if (p.length >= 16 && variety >= 3) score += 1;

  if (score < 0) score = 0;
  if (score > 4) score = 4;
  return score;
}

function strengthLabelKey(score: number): string {
  if (score <= 1) return "signup.pw.weak";
  if (score === 2) return "signup.pw.ok";
  if (score === 3) return "signup.pw.good";
  return "signup.pw.strong";
}

interface ResetWithLinkResponse {
  success?: boolean;
  message?: string;
  redirectUrl?: string;
  error?: string;
  details?: string[];
}

export function RecoveryResetView(props: { locale: Locale }) {
  // Read token from query string once on mount.
  const token = useMemo(() => {
    const u = new URL(window.location.href);
    return u.searchParams.get("token") ?? "";
  }, []);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Policy errors returned in details[] from recovery.password_policy
  const [policyErrors, setPolicyErrors] = useState<string[]>([]);

  const pwScore = useMemo(() => getPasswordStrengthScore(newPassword), [newPassword]);
  const pwPct = useMemo(() => String(Math.round((pwScore / 4) * 100)), [pwScore]);

  // Password must meet the same 12-char + complexity rules the recovery flow uses.
  // Enforce locally so the form button stays disabled until requirements are met;
  // the backend validates authoritatively on submit.
  const passwordStrength = useMemo(() => ({
    length: newPassword.length >= 12,
    uppercase: /[A-Z]/.test(newPassword),
    number: /\d/.test(newPassword),
    special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword)
  }), [newPassword]);

  const passwordValid = useMemo(
    () => passwordStrength.length && passwordStrength.uppercase && passwordStrength.number && passwordStrength.special,
    [passwordStrength]
  );

  const canSubmit = useMemo(
    () => passwordValid && newPassword === confirmPassword && token.length > 0,
    [passwordValid, newPassword, confirmPassword, token]
  );

  // Auto-redirect after success.
  useEffect(() => {
    if (!done) return;
    const timer = setTimeout(() => { window.location.href = "/login"; }, 3000);
    return () => clearTimeout(timer);
  }, [done]);

  // Missing token → show invalid-link state immediately, no form.
  const isMissingToken = token.length === 0;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || busy) return;

    setError(null);
    setPolicyErrors([]);
    setBusy(true);

    try {
      const res = await apiPost<ResetWithLinkResponse>("/v1/recovery/reset-with-link", {
        token,
        newPassword
      });

      if (!res.ok) {
        // Discriminate on the structured error key the backend sends.
        const errKey = (res.body as ResetWithLinkResponse | undefined)?.error ?? res.error ?? "";

        if (errKey === "recovery.link_invalid") {
          setError(t(props.locale, "recoveryReset.error.link_invalid"));
        } else if (errKey === "recovery.password_policy") {
          const details = (res.body as ResetWithLinkResponse | undefined)?.details ?? [];
          setPolicyErrors(details);
          setError(t(props.locale, "recoveryReset.error.password_policy"));
        } else if (errKey === "recovery.reset_failed") {
          setError(t(props.locale, "recoveryReset.error.reset_failed"));
        } else {
          setError(t(props.locale, "recoveryReset.error.generic"));
        }
        return;
      }

      setDone(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="authShell">
      <div className="authBg" />
      <div className="authWrap">
        <a className="authBrand" href="/" aria-label="KVEJA home">
          <img className="authLogo" src="/brand/kveja_logo.png" alt="KVEJA" />
        </a>

        <div className="authCard">
          <div className="authHeader">
            <h1 className="authTitle">{t(props.locale, "recoveryReset.title")}</h1>
            <p className="authSubtitle">{t(props.locale, "recoveryReset.subtitle")}</p>
          </div>

          {isMissingToken ? (
            /* No token in the URL — treat as invalid link */
            <div className="authForm">
              <div className="authError">{t(props.locale, "recoveryReset.error.link_invalid")}</div>
              <div className="authRow authRowBottom">
                <a className="btn authBtnSecondary" href="/recover">
                  {t(props.locale, "recoveryReset.startNew")}
                </a>
              </div>
            </div>
          ) : done ? (
            /* Password was set successfully */
            <div className="authForm">
              <p className="authSuccess">{t(props.locale, "recoveryReset.success")}</p>
              <p className="small">{t(props.locale, "recoveryReset.redirecting")}</p>
              <div className="authRow authRowBottom">
                <a className="btn primary authBtn" href="/login">
                  {t(props.locale, "recoveryReset.goToLogin")}
                </a>
              </div>
            </div>
          ) : (
            /* Password-set form */
            <form className="authForm" onSubmit={onSubmit}>
              {error && <div className="authError">{error}</div>}

              {policyErrors.length > 0 && (
                <ul className="authErrorList">
                  {policyErrors.map((msg, i) => (
                    <li key={i}>{msg}</li>
                  ))}
                </ul>
              )}

              <div className="authField">
                <div className="authLabel">{t(props.locale, "recoveryReset.newPassword")}</div>
                <input
                  className="authInput"
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setError(null); setPolicyErrors([]); }}
                  type="password"
                  autoComplete="new-password"
                  placeholder={t(props.locale, "recoveryReset.newPassword.placeholder")}
                  autoFocus
                  required
                />
              </div>

              {/* Password strength bar (same pattern as ResetPasswordView) */}
              <div className="pwStrengthRow" aria-live="polite">
                <div className="pwStrengthTop">
                  <div className="small">{t(props.locale, "signup.passwordStrength")}</div>
                  <div className="small">
                    <span className="pwStrengthLabel">{t(props.locale, strengthLabelKey(pwScore))}</span>
                  </div>
                </div>
                <div className="pwMeter" role="meter" aria-valuemin={0} aria-valuemax={4} aria-valuenow={pwScore}>
                  <div className="pwMeterFill" style={{ width: `${pwPct}%` }} />
                </div>
              </div>

              {/* Requirement checklist (same pattern as AccountRecoveryView password_reset step) */}
              <div className="strengthMeter">
                <div className={`strengthItem ${passwordStrength.length ? "valid" : ""}`}>
                  {t(props.locale, "recovery.password.requirement.length")}
                </div>
                <div className={`strengthItem ${passwordStrength.uppercase ? "valid" : ""}`}>
                  {t(props.locale, "recovery.password.requirement.uppercase")}
                </div>
                <div className={`strengthItem ${passwordStrength.number ? "valid" : ""}`}>
                  {t(props.locale, "recovery.password.requirement.number")}
                </div>
                <div className={`strengthItem ${passwordStrength.special ? "valid" : ""}`}>
                  {t(props.locale, "recovery.password.requirement.special")}
                </div>
              </div>

              <div className="authField">
                <div className="authLabel">{t(props.locale, "recoveryReset.confirmPassword")}</div>
                <input
                  className="authInput"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  type="password"
                  autoComplete="new-password"
                  placeholder={t(props.locale, "recoveryReset.confirmPassword.placeholder")}
                  required
                />
                {newPassword && confirmPassword && newPassword !== confirmPassword && (
                  <div className="small authError">{t(props.locale, "recovery.password.mismatch")}</div>
                )}
              </div>

              <div className="authRow authRowBottom">
                <button
                  className="btn primary authBtn"
                  disabled={!canSubmit || busy}
                  type="submit"
                >
                  <BtnSpinner loading={busy}>
                    {busy
                      ? t(props.locale, "recoveryReset.submitting")
                      : t(props.locale, "recoveryReset.submit")}
                  </BtnSpinner>
                </button>
              </div>

              <div className="authFooter">
                <a className="small" href="/login">{t(props.locale, "forgotPassword.backToLogin")}</a>
              </div>
            </form>
          )}
        </div>

        <div className="authFooter">
          <span className="small">KVEJA</span>
          <span className="authDot" aria-hidden="true">·</span>
          <a className="small" href="/">Back to landing</a>
        </div>
      </div>
    </div>
  );
}
